import React, { FC, useEffect, useMemo, useState } from 'react'
import { CheckOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { AppService, DomainService, TranslationService } from '../../../services'
import { Well } from '@pushly/aqe/lib/components'
import { DomainDto } from '../../../dtos/domain'
import { AppState } from '../../../stores/app'
import { arrayContains } from '../../../_utils/utils'
import { TypedTagSelect } from '../../../components/typed-tag-select/typed-tag-select'
import { AccountService, UserService } from '../../../services'
import { IAccountUser } from '../../../interfaces/account-user'
import { AbilityAction } from '../../../enums/ability-action.enum'
import { PasswordForm } from '../../../components/password-form/password-form'
import { Form, Input, Radio, Switch, Tooltip, FormProps, Space } from 'antd'
import { useService } from 'src/hooks/use-service'
import { InputProps } from 'antd/lib/input'
import { useForm } from 'antd/es/form/Form'
import { Rule } from 'antd/lib/form'
import { FormInstance } from 'antd/es/form'

export enum UserDataFormStyle {
    INVITE,
    CONFIRM,
    PASSWORD_RESET,
    PROFILE,
}

export interface INewUserFormOptions {
    isInternalUser: boolean
    email: string
    accounts?: IAccountUser[]
}

interface IProps extends Omit<FormProps, 'style'> {
    readonly user?: any
    readonly style?: UserDataFormStyle
    readonly className?: string
    readonly onSubmit: (error: any, values: any) => any
    readonly submitText?: string
    readonly onCancel?: () => void
    readonly cancelText?: string
    readonly hideWell?: boolean
}

interface IState {
    readonly isInternalUserSwitch?: boolean
    readonly domains: DomainDto[]
    readonly userDomains?: DomainDto[]
    userPermissions: IAccountUser[]
}

const BASE_CLASS_NAME = 'user-data-form'

const suppressEmailSymbol = (e: any): any => {
    e = e || window.event
    const charCode = typeof e.which === undefined ? e.keyCode : e.which
    const char = String.fromCharCode(charCode)
    if (/@/.test(char)) {
        e.preventDefault()
        return false
    }
}

const suppressEmailSymbolOnPaste = (e: any, form: FormInstance): any => {
    const { setFieldsValue } = form
    const value = e.clipboardData.getData('Text')

    if (/@/.test(value)) {
        e.preventDefault()
        setFieldsValue({
            email: value.replace(/@.+$/, ''),
        })
    }
}

export const UserDataForm: FC<IProps> = (props) => {
    const appState = useService(AppState)
    const appService = useService(AppService)
    const domainService = useService(DomainService)
    const accountService = useService(AccountService)
    const translationService = useService(TranslationService)
    const userService = useService(UserService)

    const [domains, setDomains] = useState<DomainDto[]>([])
    const [userPermissions, setUserPermissions] = useState<IAccountUser[]>([])
    const [isInternalUserSwitch, setIsInternalUserSwitch] = useState(props.user?.isInternalUser ?? false)

    useEffect(() => {
        const fetchUserDependencies = async (): Promise<void> => {
            if (props.style === UserDataFormStyle.PROFILE) {
                if (!props.user) {
                    setTimeout(() => fetchUserDependencies(), 50)
                    return
                }

                const { ok, data: uPerms } = await userService.fetchUserPermissions(props.user.id)

                if (ok) {
                    setUserPermissions(uPerms)
                }
            }
        }

        fetchUserDependencies()
    }, [])

    const [form] = useForm()

    // utilities
    const buildClassNames = (suffix?: string): string => {
        suffix = suffix === undefined ? '' : `-${suffix}`

        const classNames: string[] = [`${BASE_CLASS_NAME}${suffix}`]
        if (props.className) {
            classNames.push(`${props.className}${suffix}`)
        }
        return classNames.join(' ')
    }

    const propsUserIsInternalUser = props.user?.isInternalUser ?? false

    const isInviteStyle = props.style === UserDataFormStyle.INVITE
    const isConfirmStyle = props.style === UserDataFormStyle.CONFIRM
    const isPasswordResetStyle = props.style === UserDataFormStyle.PASSWORD_RESET
    const isProfileStyle = props.style === UserDataFormStyle.PROFILE

    const domainsShouldLoad = isInviteStyle || isProfileStyle
    const userTypeFieldShouldShow = domainsShouldLoad && appState.currentUser?.isInternalUser

    const isInternalUserProps = {
        disabled: !!props.user || !appState.currentUser?.isInternalUser,
        checked: propsUserIsInternalUser,
    }

    const emailFieldShouldShow = isInviteStyle || isProfileStyle
    let emailProps: InputProps = propsUserIsInternalUser
        ? {
              onKeyPress: suppressEmailSymbol,
              onPaste: (e) => suppressEmailSymbolOnPaste(e, form),
          }
        : {}
    const emailRules: Rule[] = propsUserIsInternalUser
        ? []
        : [
              {
                  type: 'email',
              },
              {
                  required: true,
                  message: 'Email is required',
              },
          ]

    const nameFieldShouldShow = isInviteStyle || isProfileStyle

    const domainsFieldShouldShow = useMemo((): boolean => {
        if (isConfirmStyle) {
            return false
        }

        const currentUser = appState.currentUser
        const currentDomain = appState.currentDomain!
        if (!currentDomain || !currentUser) {
            return false
        }

        const currentUserIsInternal = currentUser.isInternalUser
        let currentUserIsPropsUser = false
        let currentUserIsDomainAdmin = false

        if (props.user) {
            currentUserIsPropsUser = currentUser.id.toString() === props.user.id.toString()
        } else {
            if (props.style !== UserDataFormStyle.INVITE) {
                return false
            }
        }

        if (!currentUserIsInternal) {
            currentUserIsDomainAdmin = appState.abilityStore.can(AbilityAction.MANAGE, appState.currentDomain!)
        }

        return (
            !isInternalUserSwitch &&
            (isInviteStyle || isProfileStyle) &&
            !currentUserIsPropsUser &&
            (currentUserIsInternal || currentUserIsDomainAdmin)
        )
    }, [isConfirmStyle, appState.currentDomain, appState.currentUser, isInternalUserSwitch, props.user])

    let availableDomains = [...domains]
    // Constrain to domains that current platform user can administer
    if (!isPasswordResetStyle && !isConfirmStyle && !appState.currentUser?.isInternalUser) {
        const administerableDomainIds = appState.abilityStore.acs.domainIdsWhereIsAdmin

        availableDomains = availableDomains.filter((dm) => arrayContains(administerableDomainIds, dm.id))
    }

    const currentAccountId = appState.currentDomain ? appState.currentDomain.accountId : 0
    let defaultDomainSelections: any[] = []
    // Constrain to domains that current props user has access (inside available domains)
    if (userPermissions) {
        const account = userPermissions.find((au) => au.accountId === currentAccountId)

        if (account) {
            defaultDomainSelections = Object.keys(account.domainAccess).map((domainId) => {
                const config = account.domainAccess[domainId]
                const domain = availableDomains.find((d) => d.id.toString() === domainId.toString())!

                return {
                    value: (domain || {}).displayName || 'Loading...',
                    type: config.role === 90 ? 'Admin' : config.role === 10 ? 'Read Only' : 'User',
                }
            })
        }
    }

    if (props.style === UserDataFormStyle.INVITE && appState.currentDomain) {
        defaultDomainSelections = [appState.currentDomain!.displayName]
    }

    const isPropsUserAdminOnCurrentDomain = () => {
        const { abilityStore } = appState
        return abilityStore.can(AbilityAction.UPDATE, abilityStore.currentDomainIdentity)
    }

    const isPropsUserReadOnlyOnCurrentDomain = (): boolean => {
        const { abilityStore } = appState

        return (
            abilityStore.can(AbilityAction.READ, abilityStore.currentDomainIdentity) &&
            abilityStore.cannot(AbilityAction.UPDATE, abilityStore.currentDomainIdentity)
        )
    }

    const passwordFieldsShouldShow = (): boolean => {
        const styleAllowed = isConfirmStyle || isPasswordResetStyle || isProfileStyle

        if (isProfileStyle && (!props.user || props.user.id !== appState.currentUser?.id)) {
            return false
        }

        return styleAllowed
    }

    const handleSubmit = async (): Promise<any> => {
        let values: any
        let error: any
        try {
            values = await form.validateFields()
            if (values && 'domainSelections' in values && (values?.domainSelections || []).length > 0) {
                const accounts: IAccountUser[] = []

                values.domainSelections.forEach((opt: any) => {
                    const domain = domains.find((dm) => dm.displayName === opt.value)

                    if (domain) {
                        const role =
                            opt.type.toLowerCase() === 'admin' ? 90 : opt.type.toLowerCase() === 'read only' ? 10 : 50

                        const access = {
                            role,
                            flags: [],
                        }

                        const accountIdx = accounts.findIndex((a) => a.accountId === domain.accountId)
                        if (accountIdx === -1) {
                            accounts.push({
                                accountId: domain.accountId,
                                role: 0,
                                flags: [],
                                domainAccess: { [domain.id]: access },
                            })
                        } else {
                            const account = accounts.find((a) => a.accountId === domain.accountId)!
                            account.domainAccess[domain.id] = access
                            accounts.splice(accountIdx, 1, account)
                        }
                    }
                })

                if (accounts.length > 0) {
                    values.accounts = accounts
                }
            }

            if (values && 'domainAccess' in values) {
                const currentDomain = appState.currentDomain!
                const accountId = currentDomain.accountId

                const role = values.domainAccess === 'admin' ? 90 : values.domainAccess === 'read only' ? 10 : 50

                const access = {
                    role,
                    flags: [],
                }

                const accounts: IAccountUser[] = []

                if (userPermissions) {
                    const accountIdx = userPermissions.findIndex(
                        (ua) => ua.accountId.toString() === accountId.toString(),
                    )!

                    if (accountIdx === -1) {
                        accounts.push({
                            accountId,
                            role: 0,
                            flags: [],
                            domainAccess: { [currentDomain.id]: access },
                        })
                    } else {
                        const account = userPermissions.find((ua) => ua.accountId.toString() === accountId.toString())!
                        account.domainAccess[currentDomain.id] = access
                        accounts.splice(accountIdx, 1, account)
                    }
                }

                if (accounts.length > 0) {
                    values.accounts = accounts
                }
            }
        } catch (err) {
            error = err
        }

        return await props.onSubmit(error, values)
    }

    return (
        <div className={buildClassNames('wrapper')}>
            <Well
                mode={props.hideWell ? 'ghost' : 'default'}
                hideHeader={false}
                onSubmit={handleSubmit}
                submitText={props.submitText}
                onCancel={props.onCancel}
                cancelText={props.submitText}
            >
                <Form form={form} className={buildClassNames()}>
                    {userTypeFieldShouldShow && (
                        <Form.Item
                            name="isInternalUser"
                            label="Internal User"
                            initialValue={propsUserIsInternalUser}
                            required
                        >
                            <Switch
                                checkedChildren={<CheckOutlined />}
                                unCheckedChildren={<CloseOutlined />}
                                onChange={(val) => setIsInternalUserSwitch(val)}
                                {...isInternalUserProps}
                            />
                        </Form.Item>
                    )}
                    {emailFieldShouldShow && (
                        <Form.Item
                            name="email"
                            label="Email"
                            initialValue={props.user && props.user.email}
                            normalize={(value: any) => value && value.trim()}
                            rules={emailRules}
                        >
                            <Space.Compact>
                                <Input size="large" placeholder="User email address" {...emailProps} />
                                @pushly.com
                            </Space.Compact>
                        </Form.Item>
                    )}
                    {nameFieldShouldShow && (
                        <Form.Item
                            name="name"
                            label="Name"
                            initialValue={props.user && props.user.name}
                            rules={[
                                {
                                    required: true,
                                    message: 'Name is required',
                                },
                                {
                                    pattern: new RegExp('^(?!.*<.*>).*'),
                                    message: 'Name cannot contain HTML tags.',
                                },
                                { min: 2 },
                                { max: 70 },
                            ]}
                        >
                            <Input size="large" placeholder="Name" />
                        </Form.Item>
                    )}
                    {domainsFieldShouldShow && (
                        <Form.Item
                            label={
                                <Tooltip
                                    title={
                                        <div>
                                            <p>Domain access is separated into two types: User & Admin.</p>

                                            <p>
                                                <b>Admins</b> have the ability to invite, edit, and disable other users
                                                within the domain.
                                            </p>
                                        </div>
                                    }
                                >
                                    Domain Access
                                    <span className="info-icon">
                                        <InfoCircleOutlined />
                                    </span>
                                </Tooltip>
                            }
                        >
                            {availableDomains.length === 1 ? (
                                <Form.Item
                                    name="domainAccess"
                                    label="Domain Access"
                                    rules={[{ required: true }]}
                                    initialValue={
                                        props.user
                                            ? isPropsUserAdminOnCurrentDomain()
                                                ? 'admin'
                                                : isPropsUserReadOnlyOnCurrentDomain()
                                                ? 'read only'
                                                : 'standard'
                                            : 'standard'
                                    }
                                >
                                    <Radio.Group>
                                        <Radio.Button value="standard">User</Radio.Button>
                                        <Radio.Button value="read only">Read Only</Radio.Button>
                                        <Radio.Button value="admin">Admin</Radio.Button>
                                    </Radio.Group>
                                    ,
                                </Form.Item>
                            ) : (
                                <Form.Item
                                    name="domainSelections"
                                    label="Domain Access"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'At least one domain is required',
                                        },
                                    ]}
                                    initialValue={defaultDomainSelections}
                                >
                                    <TypedTagSelect
                                        className="referrer-allow"
                                        types={['User', 'Read Only', 'Admin']}
                                        placeholder="Select user domains"
                                        options={availableDomains.map((d) => ({
                                            value: d.displayName,
                                        }))}
                                    />
                                </Form.Item>
                            )}
                        </Form.Item>
                    )}
                    {passwordFieldsShouldShow() && <PasswordForm form={props.form} user={props.user} />}
                </Form>
            </Well>
        </div>
    )
}
